import React, { useState, useEffect } from 'react';
import { Users, ThumbsUp, Mail, User, Crown, Star } from 'lucide-react';
import LazyImage from './LazyImage';

const ApplicationCard = ({ application, onVote, visitorId, isLeading, canVote = true }) => {
    const [voterEmail, setVoterEmail] = useState('');
    const [isVoting, setIsVoting] = useState(false);
    const [previousVotes, setPreviousVotes] = useState(application.votes || 0);
    const [showVoteAnimation, setShowVoteAnimation] = useState(false);

    // Animate vote count changes
    useEffect(() => {
        if (application.votes && application.votes > previousVotes) {
            setShowVoteAnimation(true);
            setTimeout(() => setShowVoteAnimation(false), 1000);
        }
        setPreviousVotes(application.votes || 0);
    }, [application.votes, previousVotes]);

    const handleVoteSubmit = async (e) => {
        e.preventDefault();
        if (!voterEmail.trim()) {
            return; // Let the parent component handle the error
        }

        setIsVoting(true);
        try {
            await onVote(application.id, voterEmail.trim());
            setVoterEmail('');
        } catch (error) {
            console.error('Error voting:', error);
        } finally {
            setIsVoting(false);
        }
    };

    return (
        <div className={`bg-white rounded-xl shadow-sm border transition-all duration-300 overflow-hidden flex flex-col h-full ${isLeading
            ? 'border-yellow-400 shadow-lg ring-2 ring-yellow-200 bg-gradient-to-br from-yellow-50 to-white'
            : 'border-gray-200 hover:shadow-lg'
            }`}>
            {/* Leading Badge */}
            {isLeading && (
                <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white text-center py-1 px-2 flex-shrink-0">
                    <div className="flex items-center justify-center gap-1">
                        <Crown className="w-3 h-3" />
                        <span className="text-xs font-bold">LEADING PROJECT</span>
                        <Star className="w-3 h-3" />
                    </div>
                </div>
            )}

            {/* Project Image */}
            <div className="h-32 bg-gray-200 overflow-hidden relative flex-shrink-0">
                <LazyImage
                    src={application.image}
                    alt={application.institution}
                    className="w-full h-full"
                    fallbackSrc="/Images/background.jpg"
                />
                {isLeading && (
                    <div className="absolute top-2 right-2 bg-yellow-400 text-white p-1 rounded-full">
                        <Crown className="w-4 h-4" />
                    </div>
                )}
            </div>

            <div className="p-4 flex flex-col flex-grow">
                {/* Header with project name and votes */}
                <div className="flex justify-between items-start mb-3">
                    <div className="flex-grow pr-2">
                        <h3 className="text-lg font-bold text-gray-900 mb-1 line-clamp-2">
                            {application.institution}
                        </h3>
                        <div className="flex items-center gap-2">
                            <Users className="w-3 h-3 text-gray-500" />
                            <span className="text-xs text-gray-600 bg-blue-100 px-2 py-1 rounded-full">
                                {application.category}
                            </span>
                        </div>
                    </div>
                    <div className={`flex items-center gap-2 px-2 py-1 rounded-full flex-shrink-0 transition-all duration-300 ${showVoteAnimation ? 'scale-110 bg-green-200 border-2 border-green-400' : ''} ${isLeading
                        ? 'bg-yellow-100 border border-yellow-300'
                        : 'bg-green-50'
                        }`}>
                        <ThumbsUp className={`w-3 h-3 ${showVoteAnimation ? 'text-green-700' : ''} ${isLeading ? 'text-yellow-600' : 'text-green-600'
                            }`} />
                        <span className={`text-xs font-semibold transition-all duration-300 ${showVoteAnimation ? 'text-green-900 font-bold' : ''} ${isLeading ? 'text-yellow-900' : 'text-green-900'
                            }`}>
                            {application.votes || 0}
                            {isLeading && ' votes'}
                        </span>
                    </div>
                </div>

                {/* Team Info and Innovation Description - Scrollable */}
                <div className="mb-4 space-y-2 flex-grow overflow-hidden">
                    <div className="p-2 bg-gray-50 rounded text-xs">
                        <div className="flex items-center gap-1 mb-1">
                            <User className="w-3 h-3 text-gray-500" />
                            <span className="font-medium text-gray-700">Exhibitors:</span>
                        </div>
                        <p className="text-gray-900 font-medium line-clamp-2">{Array.isArray(application.exhibitorNames) ? application.exhibitorNames.join(', ') : application.exhibitorNames}</p>
                    </div>

                    <div className="p-2 bg-blue-50 rounded text-xs">
                        <div className="flex items-center gap-1 mb-1">
                            <Users className="w-3 h-3 text-gray-500" />
                            <span className="font-medium text-gray-700">Innovation:</span>
                        </div>
                        <p className="text-gray-900 text-xs leading-relaxed line-clamp-4">{application.innovationDescription}</p>
                    </div>
                </div>

                {/* Voting Section - Fixed at Bottom */}
                <div className="p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border mt-auto">
                    <form onSubmit={handleVoteSubmit} className="space-y-2">
                        <div className="relative">
                            <Mail className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                            <input
                                type="email"
                                placeholder="Email to vote"
                                className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs"
                                value={voterEmail}
                                onChange={(e) => setVoterEmail(e.target.value)}
                                disabled={isVoting}
                                required
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={isVoting || !voterEmail.trim()}
                            className="w-full bg-custom-green-1 text-white py-1.5 px-3 rounded hover:bg-green-700 transition-all duration-200 font-medium text-xs disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                        >
                            {isVoting ? (
                                <>
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                    Voting...
                                </>
                            ) : (
                                <>
                                    <ThumbsUp className="w-3 h-3" />
                                    Vote Now
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(ApplicationCard);